import { Combobox as BaseCombobox } from "@base-ui/react/combobox";
import { useQuery } from "@tanstack/react-query";
import { Building2, XIcon } from "lucide-react";
import { useId, useMemo } from "react";

import { getIndustries } from "@/api";
import type { IIndustryWithPolicyTypes } from "@/api/industries/industries.types";
import {
    Combobox,
    ComboboxChips,
    ComboboxChipsInput,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxItem,
    ComboboxList,
} from "@/components/ui/combobox";
import { Label } from "@/components/ui/label";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * Multi-select industry picker for the Client Hub (Add/Edit Client dialogs).
 * Design contract: docs/design-briefs/2026-06-03-industry-multicombobox.md.
 *
 * Controlled on `industry_ids: number[]`; options come from the shared
 * `["industries"]` query (5 min staleTime; a seeded cache renders without
 * a refetch). Chips use the brand search-pill recipe (`bg-primary/10
 * text-primary`) uniformly: industries are taxonomy, not status, so there
 * is deliberately no per-industry colorizer and no copper.
 *
 * Anatomy note: this is a chips+inline-input field with NO `ComboboxTrigger`,
 * which structurally moots the primitive's auto-chevron trap. If you ever add
 * a trigger, ALL its content must go inside `render={<Button …/>}` or the
 * primitive appends a second chevron (see ClientCombobox + its regression
 * test).
 */
export interface IndustryMultiComboboxProps {
    /** Selected industry ids (controlled). */
    value: number[];
    /** Fires with the full next id set on every add/remove/clear. */
    onChange: (ids: number[]) => void;
    label?: string;
    required?: boolean;
    /** Field-level error message; renders destructive border + helper text. */
    error?: string;
    disabled?: boolean;
}

export function IndustryMultiCombobox({
    value,
    onChange,
    label,
    required = false,
    error,
    disabled = false,
}: IndustryMultiComboboxProps) {
    const inputId = useId();

    const {
        data: industries,
        isPending,
        isError,
    } = useQuery({
        queryKey: ["industries"],
        queryFn: getIndustries,
        staleTime: 5 * 60 * 1000,
    });

    const options = useMemo(() => industries ?? [], [industries]);
    const selected = useMemo(
        () => options.filter((industry) => value.includes(industry.id)),
        [options, value]
    );

    const showClear = selected.length > 0 && !disabled;
    const allSelected =
        options.length > 0 && selected.length === options.length;

    return (
        <div className="flex flex-col gap-1.5">
            {label && (
                <Label htmlFor={inputId}>
                    {label}
                    {required && (
                        <span className="text-destructive ml-0.5">*</span>
                    )}
                </Label>
            )}
            <Combobox
                multiple
                items={options}
                itemToStringLabel={(industry: IIndustryWithPolicyTypes) =>
                    industry.name
                }
                value={selected}
                onValueChange={(next: IIndustryWithPolicyTypes[]) =>
                    onChange(next.map((industry) => industry.id))
                }
                openOnInputClick
                disabled={disabled || isPending || isError}
            >
                <ComboboxChips
                    className={cn(
                        "min-h-9",
                        error &&
                            "border-destructive focus-within:ring-destructive/20",
                        disabled && "pointer-events-none opacity-50"
                    )}
                >
                    {/* The input stays mounted while loading (disabled via the
                        root) so the label association never breaks; the
                        loading register is the muted line beside it. */}
                    {isPending && (
                        <span className="text-muted-foreground text-sm">
                            Loading industries…
                        </span>
                    )}
                    {!isPending && (
                        <>
                            {selected.map((industry) => (
                                <BaseCombobox.Chip
                                    key={industry.id}
                                    data-slot="combobox-chip"
                                    className="bg-primary/10 text-primary flex h-5 w-fit items-center gap-1 rounded-sm px-1.5 text-xs font-medium whitespace-nowrap"
                                >
                                    <Tooltip>
                                        <TooltipTrigger
                                            render={
                                                <span className="max-w-48 truncate" />
                                            }
                                        >
                                            {industry.name}
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            {industry.name}
                                        </TooltipContent>
                                    </Tooltip>
                                    <BaseCombobox.ChipRemove
                                        aria-label={`Remove ${industry.name}`}
                                        className="text-primary/60 hover:text-primary rounded-sm p-0.5"
                                    >
                                        <XIcon className="pointer-events-none size-3" />
                                    </BaseCombobox.ChipRemove>
                                </BaseCombobox.Chip>
                            ))}
                        </>
                    )}
                    <ComboboxChipsInput
                        id={inputId}
                        placeholder={
                            isPending || allSelected ? "" : "Select industries…"
                        }
                    />
                    {!isPending && (
                        <button
                            type="button"
                            aria-label="Clear industries"
                            tabIndex={showClear ? 0 : -1}
                            className={cn(
                                "text-muted-foreground hover:text-foreground ml-auto shrink-0 rounded-sm p-0.5 transition-opacity duration-150",
                                showClear
                                    ? "opacity-100"
                                    : "pointer-events-none opacity-0"
                            )}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                                e.stopPropagation();
                                onChange([]);
                            }}
                        >
                            <XIcon className="size-3" />
                        </button>
                    )}
                </ComboboxChips>
                <ComboboxContent>
                    <ComboboxEmpty>
                        {options.length === 0 ? (
                            // Brief state 15: deliberately no call-to-action;
                            // industries are configured on an admin surface a
                            // producer cannot reach from this dialog.
                            <span className="flex flex-col items-center gap-2 py-4">
                                <span className="bg-muted flex size-9 items-center justify-center rounded-full">
                                    <Building2 className="text-muted-foreground size-4" />
                                </span>
                                <span className="text-foreground text-sm font-medium">
                                    No industries configured
                                </span>
                            </span>
                        ) : (
                            "No industries found"
                        )}
                    </ComboboxEmpty>
                    <ComboboxList>
                        {(industry: IIndustryWithPolicyTypes) => (
                            <ComboboxItem
                                key={industry.id}
                                value={industry}
                                // Brief token map: the selected-option check
                                // is indigo, not foreground.
                                className="[&_svg]:text-primary cursor-pointer"
                            >
                                {industry.name}
                            </ComboboxItem>
                        )}
                    </ComboboxList>
                </ComboboxContent>
            </Combobox>
            {(error || isError) && (
                <p className="text-destructive text-xs">
                    {error ?? "Couldn't load industries"}
                </p>
            )}
        </div>
    );
}
